import { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '@/App';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Trophy, Loader2, Medal } from 'lucide-react';

const medal = (pos) => (pos === 1 ? 'text-yellow-500' : pos === 2 ? 'text-gray-400' : pos === 3 ? 'text-amber-700' : null);

export default function VestigiaRanking({ eventId, open, onOpenChange }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!open) return;
    setData(null); setError(null);
    axios.get(`${API}/events/${eventId}/vestigia-ranking`).then((r) => setData(r.data)).catch((e) => setError(e.response?.data?.detail || 'No se pudo cargar la clasificación'));
  }, [open, eventId]);

  const me = data?.ranking.find((r) => r.is_me);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto p-0" data-testid="vestigia-ranking-dialog">
        <DialogHeader className="p-5 pb-3 border-b sticky top-0 bg-white z-10">
          <DialogTitle className="flex items-center gap-2"><Trophy className="w-5 h-5 text-yellow-500" /> Clasificación · {data?.event_title || ''}</DialogTitle>
          <DialogDescription>Por puntos acreditados; a igualdad, por número de waypoints. {data ? `${data.total_pilots} pilotos.` : ''}</DialogDescription>
          {me && (
            <p className="text-sm font-semibold text-violet-800 bg-violet-50 rounded-md px-3 py-2 mt-1" data-testid="ranking-my-position">
              Vas en el puesto {me.position} con {me.points.toLocaleString('es-ES')} puntos y {me.verified} waypoints
            </p>
          )}
        </DialogHeader>
        <div className="p-3">
          {error && <p className="py-8 text-center text-gray-500" data-testid="ranking-error">{error}</p>}
          {!data && !error && <div className="py-10 text-center"><Loader2 className="w-7 h-7 animate-spin text-violet-600 mx-auto" /></div>}
          {data && (
            <table className="w-full text-sm" data-testid="ranking-table">
              <thead>
                <tr className="text-left text-xs text-gray-500 border-b">
                  <th className="py-2 pl-2 w-10">#</th>
                  <th className="py-2">Piloto</th>
                  <th className="py-2 text-right">WP</th>
                  <th className="py-2 text-right pr-2">Puntos</th>
                </tr>
              </thead>
              <tbody>
                {data.ranking.map((r) => (
                  <tr key={r.registration_id} className={`border-b last:border-0 ${r.is_me ? 'bg-violet-50 font-semibold' : ''}`} data-testid={`ranking-row-${r.position}`}>
                    <td className="py-2.5 pl-2">
                      {medal(r.position) ? <Medal className={`w-5 h-5 ${medal(r.position)}`} /> : <span className="text-gray-500">{r.position}</span>}
                    </td>
                    <td className="py-2.5">
                      <div className="flex items-center gap-2">
                        {r.dorsal && <span className="bg-gray-900 text-white text-[11px] font-bold rounded px-1.5">#{r.dorsal}</span>}
                        <span className="truncate">{r.name}{r.is_me ? ' (tú)' : ''}</span>
                      </div>
                      <div className="text-[11px] text-gray-500 flex gap-2 flex-wrap">
                        {r.challenge && <span>{r.challenge} {r.challenge_pct}%</span>}
                        {r.completed_challenges.map((c) => <span key={c} className="text-emerald-700 font-medium">✓ {c}</span>)}
                      </div>
                    </td>
                    <td className="py-2.5 text-right text-gray-700">{r.verified}<span className="text-gray-400">/{r.total}</span></td>
                    <td className="py-2.5 text-right pr-2 font-bold text-violet-800">{r.points.toLocaleString('es-ES')}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
